import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";

import api from "../../api";
import { getBabiesSortedByName } from "../../selectors/babies";
import { getSelectedProfile } from "../../selectors/profiles";
import { FormContext } from "../../context/form-context";
import {
  getDietDiaperRecords,
  getGrowthRecords,
  getMilestoneRecords,
  getModuleListLoading,
} from "../../selectors/core";
import { getSelectedBabyId } from "../../selectors/ui";
import { openBabyCreateModal } from "../../actions/ui";

import {
  getEnhancedBasicRecords,
  getEnhancedSleepRecords
} from "../../selectors/records";

import GrowthForm from "./GrowthForm";
import SleepForm from "./SleepForm";
import DietDiaperForm from "./DietDiaperForm";
import MilestoneForm from "./MilestoneForm";
import ActivityForm from "./ActivityForm";
import NoBabyModal from "./NoBabyModal";
import Activities, { activityList } from "./Activities";
import Modal from "../Modal";
import TrackerModal from "./TrackerChooser";
import FormDirtyWarningModal from "./FormDirtyWarningModal";
import SaveEntryModalShown from "./SaveEntryModal";
import { PrimaryButton } from "../buttons";
import { Track } from "../icons";

import "./ActivityTracker.scss";

const ActivityTracker = ({
  showTrackerModal = () => {},
  trackerModalShown = false,
  hideTrackerModals = () => {},
  milestoneModalShown = false,
  dietDiaperModalShown = false,
  growthModalShown = false,
  visitationModalShown = false,
  sleepModalShown = false,
  saveEntryModalShown = false,
  unSavedModalShown = false,
  showMilestoneModal = () => {},
  showUnSavedModal = () => {},
  showDietDiaperModal = () => {},
  showGrowthModal = () => {},
  showVisitationModal = () => {},
  showSleepModal = () => {},
  showSaveEntryModal = () => {},
  t = () => {},

  growthRecords = {},
  dietDiaperRecords = {},
  selectedProfile = {},
  profiles = [],
  sleepRecords = {},
  sleepRecordsLatest,
  sleepRecordsPatch,
  sleepRecordsPost,
  
  sleepRecordsList,
  milestoneRecordsList,
  dietDiaperRecordsList,

  growthRecordsPatch,
  growthRecordsPost,
  dietDiaperRecordsPatch,
  dietDiaperRecordsPost,
  growthRecordsLatest,
  milestoneRecordsPost,
  milestoneRecordsPatch,
  
  milestoneRecords = {},
  milestoneRecordsLatest,
  basicRecords = {},
  basicRecordsLatest,
  basicRecordsPatch,
  basicRecordsPost,
  accountId,
  babies = [],
  setFormValues,
  dietDiaperRecordsLatest,
  babiesList,
  openBabyCreateModal,
  activitiesModalShown = false,
  basicModalShown = false,
  
  ...restProps 
}) => {
  const bundledProps = {
    showTrackerModal, trackerModalShown, hideTrackerModals, milestoneModalShown,
    dietDiaperModalShown, growthModalShown, visitationModalShown, sleepModalShown,
    saveEntryModalShown, unSavedModalShown, showMilestoneModal, showUnSavedModal,
    showDietDiaperModal, showGrowthModal, showVisitationModal, showSleepModal,
    showSaveEntryModal, t, growthRecords, dietDiaperRecords, selectedProfile, profiles,
    sleepRecords, sleepRecordsLatest, sleepRecordsPatch, sleepRecordsPost,
    
    sleepRecordsList,
    milestoneRecordsList,
    dietDiaperRecordsList,

    growthRecordsPatch, growthRecordsPost, dietDiaperRecordsPatch, dietDiaperRecordsPost,
    growthRecordsLatest, milestoneRecordsPost, milestoneRecordsPatch, 
    milestoneRecords,
    milestoneRecordsLatest, basicRecords, basicRecordsLatest, basicRecordsPatch,
    basicRecordsPost, accountId, babies, setFormValues, dietDiaperRecordsLatest, babiesList,
    openBabyCreateModal, activitiesModalShown, basicModalShown, ...restProps
  };

  const [profileOptions, setProfileOptions] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [chosenActivity, setChosenActivity] = useState(null);
  const formContext = useContext(FormContext);
  const node = useRef();

  const [showFirstTimeSavingModal, setShowFirstTimeSavingModal] = useState(false);
  const [noBabiesModal, setNoBabiesModal] = useState(false);

  useEffect(() => {
    if (basicModalShown && !chosenActivity && formContext.formValues?.activityType) {
      const activity = activityList.find(a => a.value === formContext.formValues.activityType);
      if (activity) {
        setChosenActivity(activity);
      }
    }
  }, [basicModalShown]);

  useEffect(() => {
    const allRecords = {
      ...basicRecords,
      ...dietDiaperRecords,
      ...growthRecords,
      ...milestoneRecords,
      ...sleepRecords,
    };

    const hasAuthored = Object.keys(allRecords).some((key) => {
      return allRecords[key].providerId === selectedProfile.id;
    });

    setShowFirstTimeSavingModal(!hasAuthored);
  }, [
    selectedProfile,
    basicRecords,
    dietDiaperRecords,
    growthRecords,
    milestoneRecords,
    sleepRecords
  ]);

  useEffect(() => {
    const formattedProfiles = profiles.map(profile => ({
      label: `${profile.firstName} ${profile.lastName}`,
      value: profile.id
    }));

    setProfileOptions(formattedProfiles);
  }, [profiles]);

  const getRecordsByType = (type) => {
    switch (type) {
      case 'growth': return growthRecords;
      case 'dietDiaper': return dietDiaperRecords;
      case 'milestones': return milestoneRecords;
      case 'basic': return basicRecords;
      case 'sleep': return sleepRecords;
      default: return undefined;
    }
  };

  const handleRecords = (type, babyId) => {
    const records = getRecordsByType(type);
    if (!records) return;

    const recordArray = Object.keys(records)
      .map((key) => records[key])
      .sort((a, b) => {
        const aDate = new Date(a.date).valueOf();
        const bDate = new Date(b.date).valueOf();
        if (aDate === bDate) return 0;
        return aDate > bDate ? -1 : 1;
      });

    const babyRecords = babyId
      ? recordArray.filter(r => r.babyId === babyId)
      : recordArray;

    const record = babyRecords.length ? babyRecords[0] : null;
    if (!record) return null;

    const recordClone = { ...record };
    delete recordClone.babyId;
    delete recordClone.id;
    delete recordClone.createdAt;
    delete recordClone.updatedAt;
    delete recordClone.date;
    delete recordClone.startTime;
    delete recordClone.endTime;
    delete recordClone.time;

    return recordClone;
  };

  const handleClick = useCallback((e) => {
    if (node?.current?.contains(e.target)) {
      return;
    }
    setExpanded(false);
  }, []);

  const toggleWarningUnsaved = () => {
    if (formContext.isDirty) {
      return showUnSavedModal();
    } else {
      return hideTrackerModals();
    }
  };

  useEffect(() => {
    if (accountId) {
      babiesList?.({ accountId }); 
    }

    dietDiaperRecordsLatest({ accountId });
    growthRecordsLatest({ accountId });
    milestoneRecordsLatest({ accountId });
    basicRecordsLatest({ accountId });
    sleepRecordsLatest({ accountId });

    document.addEventListener("mouseup", handleClick);
    return () => {
      document.removeEventListener("mouseup", handleClick);
    };
  }, [
    accountId, 
    handleClick, 
    babiesList, 
    dietDiaperRecordsLatest, 
    growthRecordsLatest, 
    milestoneRecordsLatest, 
    basicRecordsLatest, 
    sleepRecordsLatest
  ]);

  return (
    <div className="activity-tracker__container" ref={node}>
      <TrackerModal
        {...bundledProps}
        visible={expanded}
        setExpanded={setExpanded}
        setNoBabiesModal={setNoBabiesModal}
      />

      <NoBabyModal
        noBabiesModal={noBabiesModal}
        setNoBabiesModal={setNoBabiesModal}
        openBabyCreateModal={openBabyCreateModal}
      />

      <Modal
        isOpen={milestoneModalShown && !noBabiesModal}
        onRequestClose={toggleWarningUnsaved}
      >
        <MilestoneForm
          {...bundledProps}
          milestoneRecordsPost={milestoneRecordsPost}
          milestoneRecordsPatch={milestoneRecordsPatch}
          milestoneRecordsList={milestoneRecordsList} 
          handleRecords={handleRecords}
          showFirstTimeSavingModal={showFirstTimeSavingModal}
        />
      </Modal>

      <Modal
        isOpen={growthModalShown && !noBabiesModal}
        onRequestClose={toggleWarningUnsaved}
      >
        <GrowthForm
          {...bundledProps}
          selectedProfile={selectedProfile}
          profileOptions={profileOptions}
          growthRecordsPatch={growthRecordsPatch}
          growthRecordsPost={growthRecordsPost}
          showUnSavedModal={showUnSavedModal}
          accountId={accountId}
          babies={babies}
          hideTrackerModals={hideTrackerModals}
          handleRecords={handleRecords}
          showFirstTimeSavingModal={showFirstTimeSavingModal}
        />
      </Modal>

      <Modal
        isOpen={dietDiaperModalShown && !noBabiesModal}
        onRequestClose={toggleWarningUnsaved}
      >
        <DietDiaperForm
          {...bundledProps}
          selectedProfile={selectedProfile}
          profileOptions={profileOptions}
          dietDiaperRecordsPost={dietDiaperRecordsPost}
          dietDiaperRecordsPatch={dietDiaperRecordsPatch}
          
          /* FIX: Explicitly pass Diet/Diaper refresh action */
          dietDiaperRecordsList={dietDiaperRecordsList} 
          
          accountId={accountId}
          babies={babies}
          hideTrackerModals={hideTrackerModals}
          handleRecords={handleRecords}
          showFirstTimeSavingModal={showFirstTimeSavingModal}
        />
      </Modal>

      <Modal
        isOpen={activitiesModalShown && !noBabiesModal}
        onRequestClose={toggleWarningUnsaved}
      >
        <Activities {...bundledProps} setChosenActivity={setChosenActivity} />
      </Modal>

      <Modal
        isOpen={basicModalShown && !noBabiesModal}
        onRequestClose={toggleWarningUnsaved}
      >
        <ActivityForm
          {...bundledProps}
          chosenActivity={chosenActivity}
          profileOptions={profileOptions}
          showUnSavedModal={showUnSavedModal}
          handleRecords={handleRecords}
          basicRecordsPost={basicRecordsPost}
          basicRecordsPatch={basicRecordsPatch}
          showFirstTimeSavingModal={showFirstTimeSavingModal}
        />
      </Modal>

      <Modal
        isOpen={sleepModalShown && !noBabiesModal}
        onRequestClose={toggleWarningUnsaved}
      >
        <SleepForm
          {...bundledProps}
          showUnSavedModal={showUnSavedModal}
          sleepRecordsPost={sleepRecordsPost}
          sleepRecordsPatch={sleepRecordsPatch}
          sleepRecordsList={sleepRecordsList} 
          profileOptions={profileOptions}
          showFirstTimeSavingModal={showFirstTimeSavingModal}
          handleRecords={handleRecords}
          handleFormValues={setFormValues}
        />
      </Modal>

      <SaveEntryModalShown {...bundledProps} />
      <FormDirtyWarningModal {...bundledProps} />
      <PrimaryButton
        colorScheme="basic"
        type="button"
        open={expanded}
        label={
          <Track width="32" height="32" viewBox="0 0 32 32" label="track" />
        }
        onClick={() => setExpanded(!expanded)}
      />
    </div>
  );
};

ActivityTracker.propTypes = {
  showTrackerModal: PropTypes.func,
  trackerModalShown: PropTypes.bool,
  hideTrackerModals: PropTypes.func,
  milestoneModalShown: PropTypes.bool,
  dietDiaperModalShown: PropTypes.bool,
  growthModalShown: PropTypes.bool,
  visitationModalShown: PropTypes.bool,
  sleepModalShown: PropTypes.bool,
  saveEntryModalShown: PropTypes.bool,
  unSavedModalShown: PropTypes.bool,
  showMilestoneModal: PropTypes.func,
  showDietDiaperModal: PropTypes.func,
  showUnSavedModal: PropTypes.func,
  showGrowthModal: PropTypes.func,
  showVisitationModal: PropTypes.func,
  showSleepModal: PropTypes.func,
  showSaveEntryModal: PropTypes.func,
  t: PropTypes.func,
  milestoneRecordsList: PropTypes.func,
  sleepRecordsList: PropTypes.func,
  dietDiaperRecordsList: PropTypes.func
};

const mapStateToProps = (state) => ({
  babies: getBabiesSortedByName(state),
  selectedBabyId: getSelectedBabyId(state) || getBabiesSortedByName(state)[0]?.id,
  selectedProfile: getSelectedProfile(state),
  basicRecords: getEnhancedBasicRecords(state),
  dietDiaperRecords: getDietDiaperRecords(state),
  growthRecords: state.growthRecords || {},
  milestoneRecords: getMilestoneRecords(state),
  sleepRecords: getEnhancedSleepRecords(state),
  modulesListLoading: getModuleListLoading(state)
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      basicRecordsLatest: api.actions.basicRecordsLatest,
      basicRecordsPost: api.actions.basicRecordsPost,
      basicRecordsPatch: api.actions.basicRecordsPatch,
      growthRecordsPost: api.actions.growthRecordsPost,
      growthRecordsLatest: api.actions.growthRecordsLatest,
      growthRecordsPatch: api.actions.growthRecordsPatch,
      dietDiaperRecordsLatest: api.actions.dietDiaperRecordsLatest,
      dietDiaperRecordsPost: api.actions.dietDiaperRecordsPost,
      dietDiaperRecordsPatch: api.actions.dietDiaperRecordsPatch,
      
      dietDiaperRecordsList: api.actions.dietDiaperRecordsList,
      milestoneRecordsList: api.actions.milestoneRecordsList, 
      sleepRecordsList: api.actions.sleepRecordsList,

      milestoneRecordsLatest: api.actions.milestoneRecordsLatest,
      milestoneRecordsPost: api.actions.milestoneRecordsPost,
      milestoneRecordsPatch: api.actions.milestoneRecordsPatch,
      sleepRecordsLatest: api.actions.sleepRecordsLatest,
      sleepRecordsPost: api.actions.sleepRecordsPost,
      sleepRecordsPatch: api.actions.sleepRecordsPatch,
      openBabyCreateModal: openBabyCreateModal,
      showUnSavedModal: api.actions.showUnSavedModal,
      handleFormValues: api.actions.handleFormValues
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(ActivityTracker);
